import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import { apiFetch } from '../../api.js';
import { useApi } from '../../hooks/useApi.js';
import { RouteContext } from '../../router.jsx';

export default function StaffAdminDetail({ params }) {
  const { navigate } = React.useContext(RouteContext);
  const adminId = params?.id;
  const { data, loading, error } = useApi(`/api/admins/${adminId}`, [adminId]);
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });

  React.useEffect(() => {
    if (!data) return;
    setForm({
      username: data.username || '',
      firstName: data.first_name || '',
      lastName: data.last_name || '',
      email: data.email || '',
      adminPrivilege: data.admin_privilege === 1,
      chairPrivilege: data.chair_privilege === 1,
      editPrivilege: data.edit_privilege === 1,
      activated: !!data.activated,
      ban: data.ban === 1
    });
  }, [data]);

  if (!adminId) {
    return (
      <div className="page">
        <div className="alert error">Missing admin id.</div>
      </div>
    );
  }

  const updateField = (key) => (event) => {
    const value = ['adminPrivilege', 'chairPrivilege', 'editPrivilege', 'activated', 'ban'].includes(key)
      ? event.target.checked
      : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    if (!data) return;
    setForm({
      username: data.username || '',
      firstName: data.first_name || '',
      lastName: data.last_name || '',
      email: data.email || '',
      adminPrivilege: data.admin_privilege === 1,
      chairPrivilege: data.chair_privilege === 1,
      editPrivilege: data.edit_privilege === 1,
      activated: !!data.activated,
      ban: data.ban === 1
    });
    setMessage({ text: '', tone: 'info' });
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/admins/${adminId}`, {
        method: 'PUT',
        body: JSON.stringify({
          username: form.username.trim(),
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
          admin_privilege: form.adminPrivilege ? 1 : 0,
          chair_privilege: form.chairPrivilege ? 1 : 0,
          edit_privilege: form.editPrivilege ? 1 : 0,
          activated: form.activated,
          ban: form.ban ? 1 : 0
        })
      });
      setMessage({ text: 'Admin updated.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to update admin.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!adminId) return;
    if (!window.confirm('Delete this admin? This cannot be undone.')) return;
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/admins/${adminId}`, { method: 'DELETE' });
      navigate('/staff/admins');
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to delete admin.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Admin Details</h1>
          <p className="subtle">Update privileges and access for this admin.</p>
        </div>
        <Button variant="secondary" type="button" onClick={() => navigate('/staff/admins')}>Back to Admins</Button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {loading || !form ? (
        <p className="subtle">Loading...</p>
      ) : (
        <>
          <Card title="Admin Overview">
            <div className="summary-list">
              <div className="summary-row">
                <span>Name</span>
                <span>{`${form.firstName} ${form.lastName}`.trim() || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Username</span>
                <span>{form.username || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Email</span>
                <span>{form.email || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Status</span>
                <span>{form.ban ? <Badge tone="warning">Hold</Badge> : <Badge tone="success">Active</Badge>}</span>
              </div>
              <div className="summary-row">
                <span>Privileges</span>
                <span className="badge-row">{renderPrivilegeBadges(form)}</span>
              </div>
            </div>
          </Card>

          <Card title="Edit Admin">
            <div className="form-grid">
              <Input label="Username" value={form.username} onChange={updateField('username')} />
              <Input label="First Name" value={form.firstName} onChange={updateField('firstName')} />
              <Input label="Last Name" value={form.lastName} onChange={updateField('lastName')} />
              <Input label="Email" value={form.email} onChange={updateField('email')} />
            </div>
            <div className="toggle-row">
              <div>
                <div className="label">Admin Privilege</div>
                <div className="subtle">Full admin access to staff modules.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.adminPrivilege} onChange={updateField('adminPrivilege')} />
                <span className="slider" />
              </label>
            </div>
            <div className="toggle-row">
              <div>
                <div className="label">Chair Privilege</div>
                <div className="subtle">Can finalize course change approvals.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.chairPrivilege} onChange={updateField('chairPrivilege')} />
                <span className="slider" />
              </label>
            </div>
            <div className="toggle-row">
              <div>
                <div className="label">Edit Privilege</div>
                <div className="subtle">Can edit student plans and record advisor decisions.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.editPrivilege} onChange={updateField('editPrivilege')} />
                <span className="slider" />
              </label>
            </div>
            <div className="toggle-row">
              <div>
                <div className="label">Activated</div>
                <div className="subtle">Enable login for this admin.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.activated} onChange={updateField('activated')} />
                <span className="slider" />
              </label>
            </div>
            <div className="toggle-row">
              <div>
                <div className="label">Hold Access</div>
                <div className="subtle">Temporarily disable login access.</div>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.ban} onChange={updateField('ban')} />
                <span className="slider" />
              </label>
            </div>
            <div className="form-actions">
              <Button variant="secondary" type="button" onClick={handleReset}>Reset</Button>
              <Button variant="secondary" type="button" onClick={handleDelete} disabled={saving}>Delete</Button>
              <Button type="button" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function renderPrivilegeBadges(form) {
  const badges = [];
  if (form.chairPrivilege) badges.push(<Badge key="chair" tone="success">Chair</Badge>);
  if (form.adminPrivilege) badges.push(<Badge key="admin" tone="info">Admin</Badge>);
  if (form.editPrivilege) badges.push(<Badge key="edit" tone="warning">Editor</Badge>);
  if (badges.length === 0) badges.push(<Badge key="view-only" tone="info">View Only</Badge>);
  return badges;
}
